import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Mail, Loader2, CheckCircle2, ShieldCheck, Home as HomeIcon } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/apiClient";

export default function Unsubscribe() {
  const [params] = useSearchParams();
  const prefill = params.get("email") || "";
  const [email, setEmail] = useState(prefill);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [doneMsg, setDoneMsg] = useState("");

  useEffect(() => {
    if (prefill) setEmail(prefill);
  }, [prefill]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/newsletter/unsubscribe", { email: email.trim() });
      setDone(true);
      setDoneMsg(data.message || "Désinscription confirmée.");
      toast.success("Désinscription confirmée");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Erreur, réessayez.";
      toast.error(typeof msg === "string" ? msg : "Erreur de désinscription");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0F172A] via-[#1B3A6B] to-[#0F172A] px-4 py-12"
      data-testid="page-unsubscribe"
    >
      <div className="w-full max-w-xl">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-slate-300 hover:text-white text-sm mb-8 transition-colors"
          data-testid="unsubscribe-back-home"
        >
          <HomeIcon className="w-4 h-4" /> Retour à l&apos;accueil
        </Link>

        <div className="rounded-3xl bg-white shadow-2xl p-8 sm:p-10">
          {done ? (
            <div className="text-center" data-testid="unsubscribe-success">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#3DAE2B] mb-5">
                <CheckCircle2 className="w-9 h-9 text-white" />
              </div>
              <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
                C&apos;est fait
              </h1>
              <p className="mt-3 text-slate-600 leading-relaxed">{doneMsg}</p>
              <p className="mt-6 text-xs text-slate-500">
                Si vous changez d&apos;avis, vous pouvez vous réinscrire à tout
                moment depuis notre site.
              </p>
              <Link
                to="/"
                className="mt-6 inline-flex items-center justify-center h-11 px-6 rounded-full bg-[#1B3A6B] hover:bg-[#142c54] text-white text-sm font-semibold transition-colors"
                data-testid="unsubscribe-cta-home"
              >
                Retour au site
              </Link>
            </div>
          ) : (
            <>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-100 text-slate-700 text-xs font-medium mb-5">
                <ShieldCheck className="w-3.5 h-3.5 text-[#1B3A6B]" />
                Conformité RGPD
              </div>
              <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
                Se désinscrire de la newsletter
              </h1>
              <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                Entrez l&apos;adresse email que vous souhaitez retirer de notre
                liste de diffusion. Vous ne recevrez plus aucune communication
                marketing d&apos;INGélec sur cette adresse.
              </p>

              <form onSubmit={submit} className="mt-6 space-y-3" data-testid="unsubscribe-form">
                <label htmlFor="unsub-email" className="text-xs font-medium text-slate-600 block">
                  Votre email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    id="unsub-email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="vous@exemple.com"
                    data-testid="unsubscribe-email"
                    className="w-full h-12 pl-10 pr-3 rounded-xl bg-slate-50 border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B3A6B]/30 focus:border-[#1B3A6B]"
                  />
                </div>

                <button
                  type="submit"
                  disabled={busy}
                  data-testid="unsubscribe-submit"
                  className="w-full h-12 rounded-full bg-[#1B3A6B] hover:bg-[#142c54] text-white font-semibold text-sm inline-flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
                >
                  {busy ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> Traitement…
                    </>
                  ) : (
                    "Confirmer la désinscription"
                  )}
                </button>

                <p className="text-xs text-slate-500 leading-relaxed">
                  Conformément au Règlement Général sur la Protection des
                  Données (RGPD, art. 17 et 21), vous pouvez à tout moment
                  retirer votre consentement et demander la suppression de vos
                  données personnelles.
                </p>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
